import './index.css'

import React from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { NativeCategory, NativeTag, Track, TrackId } from 'types/track'
import type { PopoverItemType } from 'types/ui'
import {
  addNativeTagToTracks,
  removeNativeTagFromTracks,
} from '@disco/state-management/actions/PlaylistTrackActions'
import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { getNativeCategories } from '@disco/state-management/selectors/tags'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import Tag from 'components/ui/tag'
import NativeTagsMenu from 'components/common/native-tags-menu'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import DialogFooter from 'components/modals/dialog-footer'
import ButtonContainer from 'components/ui/button-container'
import Icon from 'components/ui/icon'
import PopoverButton from 'components/ui/popover-button'
type CountedTag = {
  tag: NativeTag
  count: number
}
const MENU_REMOVE: PopoverItemType = {
  icon: 'close',
  value: 'close',
  label: 'Remove from all',
}
const MENU_ADD: PopoverItemType = {
  icon: 'check',
  value: 'check',
  label: 'Add to all',
}
const MAX_DISPLAY_TAGS: number = 25
type Props = {
  trackId?: TrackId
  tracks: Track[]
}

const NativeTagsTab = ({
  trackId,
  tracks,
}: Props): React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const isNativeTagsReadonly: boolean | null | undefined = useSelector(
    state => state.user.business?.private_metadata.is_native_tags_readonly,
  )
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const nativeCategories: NativeCategory[] = useSelector(getNativeCategories)
  const trackIds: TrackId[] = tracks.map((track: Track) => track.id)
  const isBulk: boolean = tracks.length > 1
  let assignedTags: CountedTag[] = []
  tracks.forEach((track: Track) => {
    let tags = track.native_tags || []
    tags.forEach((tag: NativeTag) => {
      let assigned: CountedTag | null | undefined = assignedTags.find(
        (countedTag: CountedTag) => countedTag.tag.id === tag.id,
      )

      if (!assigned) {
        assigned = {
          tag,
          count: 0,
        }
        assignedTags.push(assigned)
      }

      assigned.count += 1
    })
  })
  let table: React.ReactNode = nativeCategories
    .slice(0, MAX_DISPLAY_TAGS)
    .map((cat: NativeCategory) => {
      const values: CountedTag[] = assignedTags.filter(
        ({ tag }: CountedTag) => tag.category_id === cat.id,
      )
      let tags: React.ReactNode = values.map(
        (countedTag: CountedTag, colIdx: number) => {
          const tag: NativeTag = countedTag.tag

          if (isBulk) {
            const handleMixedMenuClick = (item: PopoverItemType) => {
              switch (item.value) {
                case MENU_REMOVE.value:
                  dispatch(removeNativeTagFromTracks(trackIds, tag))
                  break

                case MENU_ADD.value:
                  mixpanel.sendPureEvent('Add multiple native tags', {
                    Tags: [tag.name],
                  })
                  dispatch(addNativeTagToTracks(trackIds, tag))
                  break
              }
            }

            let mixed: boolean = countedTag.count < tracks.length
            let menuButton: React.ReactNode = mixed ? (
              <PopoverButton
                key="remove"
                iconNode={<Icon type="remove" width="12" height="12" />}
                items={[MENU_REMOVE, MENU_ADD]}
                onItemClick={handleMixedMenuClick}
              />
            ) : (
              <PopoverButton
                key="check"
                iconNode={<Icon type="check" width="12" height="12" />}
                items={[MENU_REMOVE]}
                onItemClick={handleMixedMenuClick}
              />
            )
            return (
              <span key={colIdx} className="NativeTagsTab_Tag">
                <Tag
                  tag={tag.name}
                  tagColor={colorIndicatorsEnabled ? tag.color : undefined}
                  action={menuButton}
                />
              </span>
            )
          } else {
            return (
              <span key={colIdx} className="NativeTagsTab_Tag">
                <Tag
                  tag={tag.name}
                  tagColor={colorIndicatorsEnabled ? tag.color : undefined}
                  onRemoveClick={
                    isNativeTagsReadonly
                      ? undefined
                      : () => {
                          dispatch(removeNativeTagFromTracks(trackIds, tag))
                        }
                  }
                />
              </span>
            )
          }
        },
      )
      return (
        <div key={cat.id} className="NativeTagsTab_Row">
          <div className="NativeTagsTab_Col NativeTagsTab_HeadCol">
            {/* merge with src/app2/views/pages/settings/track-tags */}
            <span className="tag__category">{cat.name}</span>
          </div>
          <div className="NativeTagsTab_Col">{tags}</div>
        </div>
      )
    })
  return (
    <div className="NativeTagsTab">
      <div className="NativeTagsTab_Add">
        <NativeTagsMenu
          trackIds={trackIds}
          assignedTags={assignedTags.map(({ tag }: CountedTag) => tag)}
          showLabel
        />
      </div>
      <div className="NativeTagsTab_Table">
        <div className="NativeTagsTab_Row NativeTagsTab_Head">
          <div className="NativeTagsTab_Col">Category</div>
          <div className="NativeTagsTab_Col">Tags</div>
        </div>
        {table}
      </div>
      <DialogFooter
        primary={
          <ButtonContainer>
            {!trackId && trackIds && <CopyMetadataButton trackIds={trackIds} />}
            {trackId && <CopyMetadataButton trackIds={[trackId]} />}
          </ButtonContainer>
        }
      />
    </div>
  )
}

export default NativeTagsTab
